import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { Input } from '@synoptic/ui-kit/input/input.tsx';
import { PasswordInput } from '@synoptic/ui-kit/input/password-input.tsx';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.tsx';
import { signIn } from 'aws-amplify/auth';
import { clsx } from 'clsx';
import { FC, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths';
import { sanitizeRedirectParam } from '@/routes-utils/sanitize-redirect-param.ts';
import { passwordPattern } from '../../form-utils/password-pattern';
import {
  actionLink,
  actionLinkBlock,
  cardBody,
  formFieldsContainer,
  formHeading,
  submitAuthFormButton,
} from '../_auth/page.css';

type FormInputs = {
  username: string;
  password: string;
};

export const LoginForm: FC<{
  setMfaStep: () => void;
  setNewPasswordStep: () => void;
  onLogin: () => void;
}> = ({ setMfaStep, setNewPasswordStep, onLogin }) => {
  const [searchParams] = useSearchParams();
  const fromParam = sanitizeRedirectParam(searchParams.get('from'));

  const { handleSubmit, formState, register } = useForm<FormInputs>();
  const formRef = useRef<HTMLFormElement>(null);
  const toast = useToast();
  const navigate = useNavigate();

  // stays true through post-signIn navigation, unlike formState.isSubmitting
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit: SubmitHandler<FormInputs> = async (data) => {
    setIsSubmitting(true);

    const { username, password } = data;
    try {
      const { nextStep } = await signIn({
        username,
        password,
      });

      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setMfaStep();
      } else if (
        nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
      ) {
        // FORCE_CHANGE_PASSWORD rejects `resetPassword`; must complete inline
        setNewPasswordStep();
      } else if (nextStep.signInStep === 'RESET_PASSWORD') {
        toast.info({
          title: 'Password reset required',
          subTitle: 'Please set a new password to continue.',
        });
        navigate(paths.makeResetPassword(fromParam, username), {
          replace: true,
        });
      } else {
        onLogin();
      }
    } catch (e) {
      setIsSubmitting(false);
      toast.error({ title: String(e) });
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit(submit)}>
      <h1 className={formHeading}>Welcome back</h1>
      <div className={formFieldsContainer}>
        <Input
          {...register('username', {
            required: true,
          })}
          placeholder={'Enter your email or username'}
          label={'Email or username'}
          autoComplete="username"
        />
        <PasswordInput
          {...register('password', {
            required: true,
            pattern: passwordPattern,
          })}
          placeholder={'Enter your password'}
          errorMessage={formState.errors.password?.message}
          label={'Password'}
          autoComplete="current-password"
        />
        <Link
          prefetch="intent"
          className={actionLink}
          to={paths.makeResetPassword(fromParam)}
        >
          Forgot password?
        </Link>
        <Button
          className={submitAuthFormButton}
          size={'large'}
          type="submit"
          loading={isSubmitting}
        >
          Log in
        </Button>
      </div>
      <p className={clsx(cardBody, actionLinkBlock)}>
        Don&apos;t have an account?{' '}
        <Link
          prefetch="intent"
          className={actionLink}
          to={paths.makeSignup(fromParam)}
        >
          Sign up
        </Link>
      </p>
    </form>
  );
};
